import { isEqual } from 'lodash';

export interface ReducedState<State, Cmd> {
  newState: State;
  inverseCommands: Cmd[];
}

export type Reducer<State, Cmd> = (
  currentState: State,
  cmd: Cmd
) => ReducedState<State, Cmd>;

export enum TxState {
  Open,
  Commited,
  Reverted
}

interface TxComm {
  commit(): void;
  update(): void;
}

export class Transaction<Cmd> {
  state: TxState = TxState.Open;
  commands: Cmd[] = [];
  comm: TxComm;
  constructor(comm: TxComm) {
    this.comm = comm;
  }

  isOpen() {
    return this.state === TxState.Open;
  }

  push(cmd: Cmd): number {
    if (this.state === TxState.Open) {
      this.commands.push(cmd);
      this.comm.update();
    }
    return this.commands.length - 1;
  }

  update(idx: number, updater: (cmd: Cmd) => Cmd) {
    if (this.state === TxState.Open) {
      const oldCmd = this.commands[idx];
      const newCmd = updater(this.commands[idx]);
      if (!isEqual(oldCmd, newCmd)) {
        this.commands[idx] = newCmd;
        this.comm.update();
      }
    }
  }

  revert() {
    if (this.state === TxState.Open) {
      this.state = TxState.Reverted;
      this.comm.update();
    }
  }

  commit() {
    if (this.state === TxState.Open) {
      this.state = TxState.Reverted;
      this.comm.commit();
    }
  }
}

export class UndoableState<State, Cmd> {
  commitedState: State;
  temporaryState: State;
  temporaryStateDirty = true;
  openTransactions: Transaction<Cmd>[] = [];
  undoStack: Cmd[][] = [];
  redoStack: Cmd[][] = [];
  reducer: Reducer<State, Cmd>;

  private notifyCallbacks = new Set<() => void>();
  private notifyTempCallbacks = new Set<() => void>();

  constructor(initialState: State, reducer: Reducer<State, Cmd>) {
    this.commitedState = initialState;
    this.temporaryState = initialState;
    this.reducer = reducer;
  }

  /** Subscribe to committed state changes (commit, undo, redo, replace). */
  subscribe(cb: () => void) {
    this.notifyCallbacks.add(cb);
    return () => this.notifyCallbacks.delete(cb);
  }

  /** Subscribe to temporary state changes (in-progress drag updates). */
  subscribeTemp(cb: () => void) {
    this.notifyTempCallbacks.add(cb);
    return () => this.notifyTempCallbacks.delete(cb);
  }

  private notify() {
    this.notifyCallbacks.forEach((cb) => cb());
  }

  private notifyTemp() {
    this.notifyTempCallbacks.forEach((cb) => cb());
  }

  protected markTempDirty() {
    this.temporaryStateDirty = true;
    this.notifyTemp();
  }

  protected pushUndo(cmd: Cmd[]) {
    this.undoStack.push(cmd);
    this.notify();
  }

  protected pushRedo(cmd: Cmd[]) {
    this.redoStack.push(cmd);
    this.notify();
  }

  protected flushRedo() {
    if (this.redoStack.length > 0) {
      this.redoStack.length = 0;
      this.notify();
    }
  }

  protected setCommitedState(state: State) {
    this.commitedState = state;
    this.notify();
  }

  protected processStack(state: State, cmdStack: Cmd[]) {
    const undoPacket: Cmd[] = [];
    for (const cmd of cmdStack) {
      const { newState, inverseCommands } = this.reducer(state, cmd);
      state = newState;
      undoPacket.unshift(...inverseCommands);
    }
    return { state, undoPacket };
  }

  public openTransaction(txHooks?: TxComm): Transaction<Cmd> {
    let lastUpdate = 0;
    const tx = new Transaction<Cmd>({
      commit: () => {
        txHooks?.commit();
        if (tx.commands.length > 0) {
          this.commitCommands(tx.commands);
          this.markTempDirty();
        }
      },
      update: () => {
        txHooks?.update();
        if (tx.commands.length > 0) {
          const updateId = ++lastUpdate;
          Promise.resolve().then(() => {
            if (updateId === lastUpdate && tx.state !== TxState.Commited) {
              this.markTempDirty();
            }
          });
        }
      }
    });
    this.openTransactions.push(tx);
    return tx;
  }

  /**
   * Remove all transactions from queue without commiting.
   */
  public revert() {
    this.openTransactions.length = 0;
    this.markTempDirty();
  }

  /**
   * Replace whole state with a new starting point.
   * This operation cannot be undone.
   */
  public replace(
    state: State,
    undoStack: Cmd[][] = [],
    redoStack: Cmd[][] = []
  ) {
    this.undoStack.length = 0;
    undoStack.forEach((val) => this.undoStack.push(val));
    this.redoStack.length = 0;
    redoStack.forEach((val) => this.redoStack.push(val));
    this.setCommitedState(state);
    this.markTempDirty();
    this.notify();
  }

  /**
   * Commit list of commands to state.
   * All commited commands are treated as one atomic operation and are undone together.
   */
  protected commitCommands(cmds: Cmd[]) {
    const { state, undoPacket } = this.processStack(this.commitedState, cmds);
    this.flushRedo();
    this.setCommitedState(state);
    this.pushUndo(undoPacket);
  }

  /**
   * Get current state view that includes all temporary commands.
   */
  public getTemporaryState(): Readonly<State> {
    if (this.temporaryStateDirty) {
      let state = this.commitedState;
      let needCleanup = false;
      for (const tx of this.openTransactions) {
        if (tx.state === TxState.Open) {
          ({ state } = this.processStack(state, tx.commands));
        } else {
          needCleanup = true;
        }
      }
      this.temporaryState = state;
      this.temporaryStateDirty = false;
      if (needCleanup) {
        this.openTransactions = this.openTransactions.filter(
          (tx) => tx.state === TxState.Open
        );
      }
    }
    return this.temporaryState as Readonly<State>;
  }

  /**
   * Get current state view that includes only commited commands.
   */
  public getCommitedState(): Readonly<State> {
    return this.commitedState as Readonly<State>;
  }

  /**
   * Undo single atomic set of commands.
   */
  public undo() {
    const packetToUndo = this.undoStack.pop();
    if (packetToUndo == null) {
      return;
    }

    const { state, undoPacket: redoPacket } = this.processStack(
      this.commitedState,
      packetToUndo
    );
    this.setCommitedState(state);
    this.pushRedo(redoPacket);
    this.markTempDirty();
  }

  /**
   * Redo single atomic set of undone commands.
   */
  public redo() {
    const packetToRedo = this.redoStack.pop();
    if (packetToRedo == null) {
      return;
    }

    const { state, undoPacket } = this.processStack(
      this.commitedState,
      packetToRedo
    );
    this.setCommitedState(state);
    this.pushUndo(undoPacket);
    this.markTempDirty();
  }

  /**
   * Check if undo action can be performed right now.
   */
  public canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /**
   * Check if redo action can be performed right now.
   */
  public canRedo(): boolean {
    return this.redoStack.length > 0;
  }
}
